/**
 * Story List - Matrix View with Language Columns
 *
 * Multi-language admin UI:
 * - Displays stories with aggregated translation status
 * - Language columns show phase icons for each translation
 * - Click phase icon to edit that specific translation
 */
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Link } from 'react-admin';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Checkbox,
  TablePagination,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';
import { Minus, Search, X, Trash2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// Issue #640: Renamed from StoryPhase to StoryTransPhase to clarify scope
// Issue #642: Added STORY_PHASE_CONFIG and StoryPhase for story-level lifecycle
import {
  STORY_TRANS_PHASE_CONFIG,
  STORY_PHASE_CONFIG,
  logger,
  type StoryTransPhase,
  type StoryPhase,
  type StoryListItem,
  type Language,
} from '@musedrops/shared';
import { SearchSelect } from '../../../shared/components/forms';
// Issue #851: freshness warning treatment
import { stepLabel } from '../components/FreshnessBadge';
import { supabase } from '../../../services/supabase';
import { BulkEditDialog } from '../components/BulkEditDialog';
import { BulkScheduleDialog } from '../components/BulkScheduleDialog';
import { PageToolbar } from '../../../shared/components/PageToolbar';
import { DebugBadge } from '../../../shared/components/DebugBadge';
import { listStoriesWithTrans } from '../../../data/story';
import { LanguageApi } from '../../../services/languageApi';
import { COLORS } from '../../../theme/tokens';

// Phase icon component for language cells
const PhaseIcon = ({
  phase,
  langKey,
  onClick,
  disabled,
  frontStep,
}: {
  phase: StoryTransPhase | null;
  langKey: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Issue #851: pipeline freshness front — warning background when set */
  frontStep?: string | null;
}) => {
  // Missing translation (applicable language but no translation created yet)
  if (!phase) {
    return (
      <Tooltip title="Missing translation - click to create">
        <Box
          component="button"
          onClick={e => {
            e.stopPropagation();
            onClick?.();
          }}
          disabled={disabled}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            border: 'none',
            borderRadius: 1,
            backgroundColor: 'transparent',
            cursor: disabled ? 'default' : 'pointer',
            opacity: 0.5,
            '&:hover': { backgroundColor: 'action.hover', opacity: 1 },
          }}
        >
          <Typography variant="caption" sx={{ textTransform: 'uppercase' }}>
            {langKey}
          </Typography>
          <span style={{ fontSize: '1rem' }}>🚫</span>
        </Box>
      </Tooltip>
    );
  }

  const config = STORY_TRANS_PHASE_CONFIG[phase];
  if (!config) return null;

  // Issue #851: not at the final artifact → warning treatment
  const title = frontStep
    ? `${config.label} — pipeline at: ${stepLabel(frontStep)}`
    : config.label;

  return (
    <Tooltip title={title}>
      <Box
        component="button"
        onClick={e => {
          e.stopPropagation();
          onClick?.();
        }}
        disabled={disabled}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          py: 0.25,
          // Issue #851: frame (not fill) so the warning treatment stays legible
          // in dark mode; transparent border keeps box sizing stable when absent.
          border: '1px solid',
          borderColor: frontStep ? 'warning.main' : 'transparent',
          borderRadius: 1,
          backgroundColor: 'transparent',
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Typography variant="caption" sx={{ textTransform: 'uppercase' }}>
          {langKey}
        </Typography>
        <span style={{ fontSize: '1rem' }}>{config.emoji}</span>
      </Box>
    </Tooltip>
  );
};

const StoryPhaseIcon = ({
  phase,
  frontStep,
}: {
  phase: StoryPhase | string | null;
  /** Issue #851: story-pipeline freshness front — warning background when set */
  frontStep?: string | null;
}) => {
  if (!phase) return null;

  const config = STORY_PHASE_CONFIG[phase as StoryPhase];
  if (!config) return <span style={{ fontSize: '1rem' }}>❓</span>;

  const title = frontStep
    ? `${config.label} — pipeline at: ${stepLabel(frontStep)}`
    : `${config.label}: ${config.description}`;

  return (
    <Tooltip title={title}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          px: 0.5,
          // Issue #851: frame instead of fill — legible in dark mode
          border: '1px solid',
          borderColor: frontStep ? 'warning.main' : 'transparent',
          borderRadius: 1,
        }}
      >
        <span style={{ fontSize: '1rem' }}>{config.emoji}</span>
      </Box>
    </Tooltip>
  );
};

// Show field with link to show edit page
const ShowCell = ({ story }: { story: StoryListItem }) => {
  return (
    <Link
      to={`/shows/${story.showId}/edit`}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {story.showSlug}
      </Typography>
    </Link>
  );
};

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type FilterChangeHandler = (value: string) => void;

interface StoryListActionsProps {
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  // Filters
  showSlugFilter: string;
  onShowSlugChange: FilterChangeHandler;
  clusterIdFilter: string;
  onClusterIdChange: FilterChangeHandler;
  searchQuery: string;
  onSearchQueryChange: FilterChangeHandler;
  onSearchSubmit: () => void;
  onClearSearch: () => void;
  shows: Array<{ slug: string; name: string }>;
  clusters: Array<{ id: string; description: string | null }>;
}

const StoryListActions = ({
  onClearFilters,
  hasActiveFilters,
  showSlugFilter,
  onShowSlugChange,
  clusterIdFilter,
  onClusterIdChange,
  searchQuery,
  onSearchQueryChange,
  onSearchSubmit,
  onClearSearch,
  shows,
  clusters,
}: StoryListActionsProps) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const activeColor =
    theme.palette.mode === 'dark'
      ? COLORS.activeFilterBackgroundDark
      : COLORS.activeFilterBackgroundLight;

  const handleGenerateStories = () => {
    const params = showSlugFilter ? `?show=${encodeURIComponent(showSlugFilter)}` : '';
    navigate(`/stories/create${params}`);
  };

  return (
    <PageToolbar
      left={
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <SearchSelect
            label="Show"
            placeholder="All shows"
            options={shows.map(show => ({
              id: show.slug,
              label: show.name,
              slug: show.slug,
            }))}
            value={showSlugFilter || null}
            onChange={value => onShowSlugChange(value ?? '')}
            width="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: showSlugFilter ? activeColor : 'transparent',
              },
            }}
          />

          <SearchSelect
            label="Cluster"
            placeholder="All clusters"
            options={clusters.map(c => ({
              id: c.id,
              label: `${c.id} - ${c.description || 'No description'}`,
            }))}
            value={clusterIdFilter || null}
            onChange={value => onClusterIdChange(value ?? '')}
            valueField="id"
            width="small"
            popperMinWidth={400}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: clusterIdFilter ? activeColor : 'transparent',
              },
            }}
          />

          <TextField
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => onSearchQueryChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearchSubmit()}
            sx={{
              width: 200,
              '& .MuiOutlinedInput-root': {
                backgroundColor: searchQuery ? activeColor : 'transparent',
                '&:hover': {
                  backgroundColor: searchQuery ? activeColor : 'transparent',
                },
                '&.Mui-focused': {
                  backgroundColor: searchQuery ? activeColor : 'transparent',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={onClearSearch}
                    edge="end"
                    sx={{ mr: -1 }}
                  >
                    <X size={18} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {hasActiveFilters && (
            <Button variant="outlined" size="small" onClick={onClearFilters}>
              Clear Filters
            </Button>
          )}
        </Box>
      }
      right={
        <Button variant="contained" onClick={handleGenerateStories}>
          Add Stories
        </Button>
      }
    />
  );
};

const EmptyState = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '50vh',
        width: '100%',
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" gutterBottom>
        No stories yet
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Generate stories to get started
      </Typography>
      <Button variant="contained" onClick={() => navigate('/stories/create')}>
        Generate Stories
      </Button>
    </Box>
  );
};

// Sort configuration types
type SortField = 'topic' | 'showSlug' | 'publishedAt' | 'updatedAt';
type SortOrder = 'asc' | 'desc';

// Format relative time
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Parse filter from URL (supports both JSON format and simple params)
const parseFilterFromUrl = (
  searchParams: URLSearchParams
): { showSlug?: string; clusterId?: string; q?: string } => {
  // Try JSON format first (from ShowList link)
  const filterJson = searchParams.get('filter');
  if (filterJson) {
    try {
      return JSON.parse(filterJson);
    } catch {
      // Ignore parse errors
    }
  }

  // Fallback to simple params
  return {
    showSlug: searchParams.get('showSlug') || undefined,
    clusterId: searchParams.get('clusterId') || undefined,
    q: searchParams.get('q') || undefined,
  };
};

export const StoryList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [stories, setStories] = useState<StoryListItem[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Bulk action dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sorting (default: published desc)
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Applied filters from URL (source of truth for data loading)
  const appliedFilters = useMemo(
    () => parseFilterFromUrl(searchParams),
    [searchParams]
  );

  // Local filter state (for UI input)
  const [showSlugFilter, setShowSlugFilter] = useState<string>(
    appliedFilters.showSlug || ''
  );
  const [clusterIdFilter, setClusterIdFilter] = useState<string>(
    appliedFilters.clusterId || ''
  );
  const [searchQuery, setSearchQuery] = useState<string>(
    appliedFilters.q || ''
  );

  // Shows for filter dropdown
  const [shows, setShows] = useState<Array<{ slug: string; name: string }>>([]);
  // Clusters for filter dropdown
  const [clusters, setClusters] = useState<Array<{ id: string; description: string | null }>>([]);

  // Track if we're updating URL from state (to avoid infinite loops)
  const isUpdatingUrl = useRef(false);

  // Sync local state FROM URL (when URL changes externally, e.g., browser back/forward)
  useEffect(() => {
    if (isUpdatingUrl.current) {
      isUpdatingUrl.current = false;
      return;
    }
    setShowSlugFilter(appliedFilters.showSlug || '');
    setClusterIdFilter(appliedFilters.clusterId || '');
    setSearchQuery(appliedFilters.q || '');
  }, [appliedFilters]);

  // Active filters based on URL (applied filters)
  const hasActiveFilters = Boolean(appliedFilters.showSlug || appliedFilters.clusterId || appliedFilters.q);

  // Update URL (applies the filters)
  const applyFilters = useCallback(
    (newShowSlug: string, newClusterId: string, newSearchQuery: string) => {
      isUpdatingUrl.current = true;
      const params: Record<string, string> = {};
      if (newShowSlug) params.showSlug = newShowSlug;
      if (newClusterId) params.clusterId = newClusterId;
      if (newSearchQuery) params.q = newSearchQuery;
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  // Show dropdown changes apply immediately
  const handleShowSlugChange = useCallback(
    (value: string) => {
      setShowSlugFilter(value);
      applyFilters(value, appliedFilters.clusterId || '', appliedFilters.q || '');
    },
    [appliedFilters.clusterId, appliedFilters.q, applyFilters]
  );

  // Cluster dropdown changes apply immediately
  const handleClusterIdChange = useCallback(
    (value: string) => {
      setClusterIdFilter(value);
      applyFilters(appliedFilters.showSlug || '', value, appliedFilters.q || '');
    },
    [appliedFilters.showSlug, appliedFilters.q, applyFilters]
  );

  // Search text changes don't apply immediately
  const handleSearchQueryChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  // Search submit applies the filter (on Enter)
  const handleSearchSubmit = useCallback(() => {
    applyFilters(showSlugFilter, clusterIdFilter, searchQuery);
  }, [showSlugFilter, clusterIdFilter, searchQuery, applyFilters]);

  // Clear search button clears both local state and applies
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    applyFilters(showSlugFilter, clusterIdFilter, '');
  }, [showSlugFilter, clusterIdFilter, applyFilters]);

  // Get global languages (for column headers)
  const globalLanguages = useMemo(() => {
    return languages.filter(lang => lang.includeInGlobalGen);
  }, [languages]);

  // Load languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const langs = await LanguageApi.getAll();
        setLanguages(langs);
      } catch (error) {
        logger.error('Failed to load languages', { error });
      }
    };
    loadLanguages();
  }, []);

  // Load shows for filter
  useEffect(() => {
    const loadShows = async () => {
      const { data, error } = await supabase
        .from('shows')
        .select('slug')
        .eq('enabled', true)
        .order('slug');
      if (!error && data) {
        setShows(data.map(s => ({ slug: s.slug, name: s.slug })));
      }
    };
    loadShows();
  }, []);

  // Load clusters for filter
  useEffect(() => {
    const loadClusters = async () => {
      const { data, error } = await supabase
        .from('clusters')
        .select('id, description')
        .order('id');
      if (!error && data) {
        setClusters(data);
      }
    };
    loadClusters();
  }, []);

  // Load stories based on APPLIED filters (from URL)
  const loadStories = useCallback(async () => {
    setLoading(true);
    try {
      const filter: Record<string, unknown> = {};
      if (appliedFilters.showSlug) filter.showSlug = appliedFilters.showSlug;
      if (appliedFilters.clusterId) filter.clusterId = appliedFilters.clusterId;
      if (appliedFilters.q) filter.q = appliedFilters.q;

      const result = await listStoriesWithTrans({
        page: page + 1,
        perPage: rowsPerPage,
        filter,
        withTotal: true,
        sortField,
        sortOrder: sortOrder.toUpperCase() as 'ASC' | 'DESC',
      });

      setStories(result.data);
      setTotal(result.total ?? result.data.length);
    } catch (error) {
      logger.error('Failed to load stories', { error });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, appliedFilters, sortField, sortOrder]);

  useEffect(() => {
    loadStories();
  }, [loadStories]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('stories-list')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'stories' },
        () => loadStories()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'story_translations' },
        () => loadStories()
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [loadStories]);

  // Visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadStories();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadStories]);

  // Handlers
  const handleRowClick = (e: React.MouseEvent, storyId: string) => {
    const url = `/stories/${storyId}/edit`;
    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank');
      return;
    }
    navigate(url);
  };

  const handleLanguageClick = (storyId: string, lang: string) => {
    navigate(`/stories/${storyId}/edit?tab=${lang}`);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelected(checked ? stories.map(s => s.id) : []);
  };

  const handleSelectOne = (storyId: string, checked: boolean) => {
    setSelected(prev =>
      checked ? [...prev, storyId] : prev.filter(id => id !== storyId)
    );
  };

  const handleClearFilters = useCallback(() => {
    setShowSlugFilter('');
    setClusterIdFilter('');
    setSearchQuery('');
    isUpdatingUrl.current = true;
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Delete selected stories (cascade deletes translations via FK)
  const handleDeleteSelected = useCallback(async () => {
    if (selected.length === 0) return;

    setDeleting(true);
    try {
      // Delete stories - translations are automatically cascade-deleted via FK
      const { error } = await supabase
        .from('stories')
        .delete()
        .in('id', selected);

      if (error) {
        logger.error('Failed to delete stories', {
          error,
          count: selected.length,
        });
        return;
      }

      logger.info('Deleted stories', { count: selected.length });
      setSelected([]);
      setDeleteDialogOpen(false);
      // Reload will happen via realtime subscription
    } catch (error) {
      logger.error('Error deleting stories', { error });
    } finally {
      setDeleting(false);
    }
  }, [selected]);

  // Callback when bulk edit/schedule completes
  const handleBulkActionComplete = useCallback(() => {
    setSelected([]);
    // Reload will happen via realtime subscription, but force immediate refresh
    loadStories();
  }, [loadStories]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // New field, default to desc
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(0); // Reset to first page on sort change
  };

  if (loading && stories.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && stories.length === 0 && !hasActiveFilters) {
    return <EmptyState />;
  }

  return (
    <Box>
      <StoryListActions
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        showSlugFilter={showSlugFilter}
        onShowSlugChange={handleShowSlugChange}
        clusterIdFilter={clusterIdFilter}
        onClusterIdChange={handleClusterIdChange}
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        onSearchSubmit={handleSearchSubmit}
        onClearSearch={handleClearSearch}
        shows={shows}
        clusters={clusters}
      />

      {/* Bulk Action Bar */}
      {selected.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            mb: 1,
            backgroundColor: 'action.selected',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {selected.length} {selected.length === 1 ? 'story' : 'stories'}{' '}
            selected
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setEditDialogOpen(true)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setScheduleDialogOpen(true)}
          >
            Schedule
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small" sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 'auto' }}>
                <Checkbox
                  indeterminate={
                    selected.length > 0 && selected.length < stories.length
                  }
                  checked={
                    stories.length > 0 && selected.length === stories.length
                  }
                  onChange={e => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              {/* Issue #642: Story phase column */}
              <TableCell
                align="center"
                sx={{ width: 40, minWidth: 40, px: 0.5 }}
              >
                <Tooltip title="Story Phase">
                  <span style={{ fontSize: '0.75rem' }}>Ph</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ width: 'auto' }}>
                <TableSortLabel
                  active={sortField === 'topic'}
                  direction={sortField === 'topic' ? sortOrder : 'desc'}
                  onClick={() => handleSort('topic')}
                >
                  Story
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'showSlug'}
                  direction={sortField === 'showSlug' ? sortOrder : 'desc'}
                  onClick={() => handleSort('showSlug')}
                >
                  Show
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                Cluster
              </TableCell>
              {globalLanguages.map(lang => (
                <TableCell
                  key={lang.key}
                  align="center"
                  sx={{ width: 40, minWidth: 40, px: 0.5 }}
                >
                  <Tooltip title={lang.name}>
                    <span style={{ fontSize: '0.75rem' }}>
                      {lang.key.toUpperCase()}
                    </span>
                  </Tooltip>
                </TableCell>
              ))}
              <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'publishedAt'}
                  direction={sortField === 'publishedAt' ? sortOrder : 'desc'}
                  onClick={() => handleSort('publishedAt')}
                >
                  Published
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'updatedAt'}
                  direction={sortField === 'updatedAt' ? sortOrder : 'desc'}
                  onClick={() => handleSort('updatedAt')}
                >
                  Updated
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stories.map(story => {
              const isSelected = selected.includes(story.id);
              // Determine which languages apply to this story
              const applicableLangs = story.showLang
                ? [story.showLang] // Single-language show
                : globalLanguages.map(l => l.key); // All languages

              return (
                <TableRow
                  key={story.id}
                  hover
                  selected={isSelected}
                  onClick={(e) => handleRowClick(e, story.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={e => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={e =>
                        handleSelectOne(story.id, e.target.checked)
                      }
                    />
                  </TableCell>
                  {/* Issue #642: Story phase column */}
                  <TableCell align="center" sx={{ px: 0.5 }}>
                    <StoryPhaseIcon
                      phase={story.phase as StoryPhase | null}
                      frontStep={story.frontStep}
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/stories/${story.id}/edit`}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {story.topic || 'No topic'}
                        </Typography>
                        {Object.values(story.translations || {}).some(
                          t => t?.usedDebug
                        ) && <DebugBadge show />}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.2 }}
                        noWrap
                      >
                        {story.slug}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <ShowCell story={story} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      noWrap
                    >
                      {story.clusterId || '—'}
                    </Typography>
                  </TableCell>
                  {globalLanguages.map(lang => {
                    const isApplicable = applicableLangs.includes(lang.key);
                    const translation = story.translations?.[lang.key];
                    const phase = translation?.phase as StoryTransPhase | null;

                    return (
                      <TableCell key={lang.key} align="center" sx={{ px: 0.5 }}>
                        {isApplicable ? (
                          // Issue #767: show 🚫 in place of the phase icon when disabled
                          translation?.enabled === false ? (
                            <Tooltip
                              title={`Disabled: ${translation.disabledReason || 'No reason'}`}
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  opacity: 0.7,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ textTransform: 'uppercase' }}
                                >
                                  {lang.key}
                                </Typography>
                                <span style={{ fontSize: '1rem' }}>🚫</span>
                              </Box>
                            </Tooltip>
                          ) : (
                            <PhaseIcon
                              phase={phase}
                              langKey={lang.key}
                              frontStep={translation?.frontStep}
                              onClick={() =>
                                handleLanguageClick(story.id, lang.key)
                              }
                            />
                          )
                        ) : (
                          <Tooltip title="Not applicable for this show">
                            <Box sx={{ opacity: 0.2 }}>
                              <Minus size={14} />
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                    >
                      {story.publishedAt
                        ? new Date(
                            story.publishedAt as string
                          ).toLocaleDateString('en-CA')
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                    >
                      {story.updatedAt
                        ? formatRelativeTime(story.updatedAt as string)
                        : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Stories</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length}{' '}
            {selected.length === 1 ? 'story' : 'stories'}?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            All translations for{' '}
            {selected.length === 1 ? 'this story' : 'these stories'} will also
            be deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSelected}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <Trash2 size={16} />
            }
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <BulkEditDialog
        open={editDialogOpen}
        selectedIds={selected}
        onClose={() => setEditDialogOpen(false)}
        onComplete={handleBulkActionComplete}
      />

      {/* Bulk Schedule Dialog */}
      <BulkScheduleDialog
        open={scheduleDialogOpen}
        selectedIds={selected}
        onClose={() => setScheduleDialogOpen(false)}
        onComplete={handleBulkActionComplete}
      />
    </Box>
  );
};
